/**
 * The ADJUSTED design: a remeasure session projected back onto a sketch.
 *
 * `remeasure-session.js` freezes the designer's document and records field
 * readings against a SEPARATE dataset — it "never writes back into geometry",
 * deliberately, so Engineering receives a complete set while the designer's
 * original stays intact. This module does not change that. It builds a THIRD
 * document from the two, for drawing only. The session's `originalSketch` is
 * never mutated, and neither is the document that was handed in.
 *
 * WHY THE ROOM DOES NOT CLOSE, AND WHY THAT NEEDS NO MECHANISM
 *
 * The owner, ruling on what a remeasure drawing does when the field numbers do
 * not form a loop (RS-9 part 4): *"remove the snap closed and scale to actual
 * scale (v. adjusting to keep room closed)."*
 *
 * A room is a closed chain and a technician's numbers come back independent, so
 * NOT closing is the normal case. `solveClosure` only redistributes when
 * `!openChain && free.length > 0` — it stretches FREE (unmeasured) walls to
 * absorb the gap. Every wall here comes back **measured**, so there are no free
 * walls, nothing is moved, and the residual is reported rather than hidden.
 * The ruling falls out of the data; the solver needs no special case and gets
 * none. `snapClosed` is set false as well, because the owner said so in as many
 * words and because the visual closure layer is a separate mechanism from the
 * solver.
 *
 * WHAT IS REFUSED RATHER THAN GUESSED
 *
 * Two readings cannot be projected back faithfully, and both are ruled (ADR
 * 0098). The obstruction INSET is stored as `Math.max(0, -faceOffsetUnits)`, so
 * a technician types a positive number and the reading cannot tell a flush face
 * from a proud one; the original's SIGN is kept and only the magnitude is
 * taken. The ceiling DIAMETER is read as `diameterUnits ?? widthUnits`, so
 * which field supplied it is known per feature; it is written back to that same
 * field rather than normalised onto one, which would be a silent schema change.
 *
 * A row naming something the sketch does not contain is collected into
 * `remeasureRefusals` rather than dropped, so the report can say what it could
 * not apply instead of quietly showing a drawing that is missing it.
 */
import { deserializeRemeasureSession } from './remeasure-session.js';

const clone = (value) => JSON.parse(JSON.stringify(value));
const finite = (value) => (Number.isFinite(value) ? value : null);

/**
 * Builds the adjusted sketch for a remeasure session.
 *
 * @param {object} session a remeasure session (validated on the way in)
 * @returns {object} a serialized sketch carrying the remeasured figures, with
 *   `snapClosed: false` and a `remeasureRefusals` array of `{key, reason}`.
 */
export function applyRemeasure(session) {
  const valid = deserializeRemeasureSession(session);
  const doc = clone(valid.originalSketch);
  const refusals = [];

  const wallById = new Map((doc.walls ?? []).map((w) => [w.id, w]));
  const obstructionById = new Map((doc.obstructions ?? []).map((o) => [o.id, o]));
  const openingById = new Map((doc.ceilingOpenings ?? []).map((c) => [c.id, c]));

  // Which field each ceiling diameter was READ from, decided the same way
  // `productionMeasurements` decides it, and captured BEFORE anything is
  // written — once a value lands in `diameterUnits`, asking again would give a
  // different answer than the reading did.
  const diameterField = new Map();
  for (const feature of doc.ceilingOpenings ?? []) {
    diameterField.set(feature.id, Number.isFinite(feature.diameterUnits) ? 'diameterUnits' : 'widthUnits');
  }

  const refuse = (key, reason) => { refusals.push({ key, reason }); };

  for (const row of valid.measurements) {
    const value = finite(row.actualUnits);
    if (value === null) { refuse(row.key, 'The remeasured value is not a number.'); continue; }
    const parts = String(row.key).split(':');

    if (parts[0] === 'wall' && parts[2] === 'length') {
      const wall = wallById.get(parts[1]);
      if (!wall) { refuse(row.key, 'That wall is no longer in the sketch.'); continue; }
      wall.lengthUnits = value;
      // MEASURED is the load-bearing half. A wall left free would be stretched
      // by the closure solver to absorb the room's gap, which is exactly what
      // the owner ruled against.
      wall.isMeasured = true;
      continue;
    }

    if (parts[0] === 'room') {
      if (parts[1] === 'ceilingHeight') { doc.ceilingHeightUnits = value; continue; }
      if (parts[1] === 'doorHeight') { doc.doorHeightUnits = value; continue; }
      refuse(row.key, 'That room measurement is not one this report can apply.');
      continue;
    }

    if (parts[0] === 'corner' && parts[2] === 'height') {
      if (!wallById.has(parts[1])) { refuse(row.key, 'That wall is no longer in the sketch.'); continue; }
      doc.cornerHeights = doc.cornerHeights ?? {};
      doc.cornerHeights[parts[1]] = value;
      continue;
    }

    if (parts[0] === 'obstruction') {
      const item = obstructionById.get(parts[1]);
      if (!item) { refuse(row.key, 'That obstruction is no longer in the sketch.'); continue; }
      applyObstruction(item, parts[2], value, row.key, refuse);
      continue;
    }

    if (parts[0] === 'ceiling') {
      const feature = openingById.get(parts[1]);
      if (!feature) { refuse(row.key, 'That ceiling opening is no longer in the sketch.'); continue; }
      applyCeiling(feature, parts[2], value, diameterField.get(parts[1]), row.key, refuse);
      continue;
    }

    refuse(row.key, 'That measurement is not one this report knows how to apply.');
  }

  // "Remove the snap closed" — the owner's words. The solver already leaves the
  // chain alone (no free walls), and this turns off the separate VISUAL closure
  // layer so nothing nudges the drawing either.
  doc.snapClosed = false;
  doc.remeasureRefusals = refusals;
  return doc;
}

/** One obstruction field. `inset` is the ruled special case. */
function applyObstruction(item, field, value, key, refuse) {
  switch (field) {
    case 'offsetAlongWall': item.offsetAlongWall = value; return;
    case 'offsetFromFloor': item.offsetFromFloor = value; return;
    case 'width': item.widthUnits = value; return;
    case 'height': item.heightUnits = value; return;
    case 'depth': item.depthUnits = value; return;
    case 'clearOpeningWidth':
      if (!item.opening) { refuse(key, 'That opening no longer has casing details.'); return; }
      item.opening.clearOpeningWidthUnits = value;
      return;
    case 'swingProjection':
      if (!item.opening) { refuse(key, 'That opening no longer has swing details.'); return; }
      item.opening.swingProjectionUnits = value;
      return;
    case 'inset': {
      // NEVER guess the sign. The reading is `max(0, -faceOffsetUnits)`, which
      // is 0 for a flush face AND 0 for a proud one, so it cannot distinguish
      // them. Keep the original's sign and take the magnitude only: a proud
      // face stays proud, a recess stays a recess at the remeasured depth.
      const original = Number.isFinite(item.faceOffsetUnits) ? item.faceOffsetUnits : 0;
      if (original > 0) return;             // proud — the technician never stated this
      // `-Math.abs(0)` is NEGATIVE zero, which `Object.is` separates from 0 and
      // `JSON.stringify` flattens to 0 — so a flush face would compare unequal
      // to itself before a save and equal after one. Normalised here rather
      // than left for a reader to trip over.
      item.faceOffsetUnits = value === 0 ? 0 : -Math.abs(value);
      return;
    }
    default:
      refuse(key, 'That obstruction measurement is not one this report can apply.');
  }
}

/** One ceiling-opening field. `diameter` writes back where it was read from. */
function applyCeiling(feature, field, value, diameterFieldName, key, refuse) {
  switch (field) {
    case 'fromReference1': feature.xUnits = value; return;
    case 'fromReference2': feature.yUnits = value; return;
    case 'fromFloor': feature.fromFloorUnits = value; return;
    case 'width': feature.widthUnits = value; return;
    case 'length': feature.lengthUnits = value; return;
    case 'drop': feature.heightUnits = value; return;
    case 'stairFloorBase': feature.ladderFloorOffsetUnits = value; return;
    case 'diameter':
      // Whichever field the reading came from, and no other — normalising onto
      // one would change the document's shape under cover of a remeasure.
      feature[diameterFieldName ?? 'widthUnits'] = value;
      return;
    default:
      refuse(key, 'That ceiling measurement is not one this report can apply.');
  }
}
